import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useDataTable } from '@composables/useDataTable';
import type { DataTableData } from '@composables/useDataTable';
import type {
  DataTablePageEvent,
  DataTableSortEvent,
  DataTableFilterEvent,
  DataTableFilterMeta,
} from 'primevue/datatable';
import type { DataViewPageEvent } from 'primevue';
import { FilterMatchMode } from '@primevue/core/api';
import { Logger } from '@tools/logger/logger';
import { Requests } from '@tools/requests';
import { flashBagError } from '@tools/flashbag';
import { trans } from '@elements/translation';

export const useDataTableStore = defineStore('dataTable', () => {
  const endpoint = ref<string | undefined>();

  const {
    config,
    data,
    setData,
    getApiConfig,
    reset: resetDataTable,
    setDataKey,
  } = useDataTable();

  // eslint-disable-next-line max-len
  const isPrimevueEvent = (p: DataTableFilterMeta | DataTableFilterEvent): p is DataTableFilterEvent => 'filters' in p && p.filters !== undefined && 'originalEvent' in p && p.originalEvent !== undefined;

  const createPrimevueEvent = (input: DataTableFilterMeta | DataTableFilterEvent, originalEvent: Event = new Event('filter')): DataTableFilterEvent => {
    const filters = isPrimevueEvent(input) ? input.filters : input;
    return {
      originalEvent,
      first: 0,
      rows: config.value.perPage,
      sortField: undefined,
      sortOrder: undefined,
      multiSortMeta: undefined,
      filters,
      filterMatchModes: undefined,
      filteredValue: undefined,
    };
  };

  const getData = async (): Promise<DataTableData | null> => {
    if (!endpoint.value) {
      flashBagError(trans('global.error'));
      return null;
    }
    const apiConfig = getApiConfig(config.value);

    try {
      // eslint-disable-next-line no-shadow
      const { data } = await Requests.get(endpoint.value, { params: { ...apiConfig } });
      return data;
    } catch (err) {
      Logger.error(err);
      return null;
    }
  };

  const onPageChange = async (event: DataTablePageEvent | DataViewPageEvent, shouldReload: boolean = true): Promise<void> => {
    config.value.page = 1 + event.page;

    if (shouldReload) {
      data.value = await getData();
    }
  };

  const onSortChange = async (event: DataTableSortEvent, shouldReload: boolean = true): Promise<void> => {
    config.value.sort.field = event.sortField ?? '';
    config.value.sort.direction = event.sortOrder === 1 ? 'asc' : 'desc';
    config.value.page = 1;

    if (shouldReload) {
      data.value = await getData();
    }
  };

  const onFilterChange = async ({ filters }: DataTableFilterEvent, shouldReload: boolean = true): Promise<void> => {
    config.value.filters = filters;
    config.value.page = 1;

    if (shouldReload) {
      data.value = await getData();
    }
  };

  const addFilter = async (
    field: string,
    value: unknown,
    matchMode: string = FilterMatchMode.CONTAINS,
    shouldReload: boolean = true,
  ): Promise<void> => {
    const filters: DataTableFilterMeta = {
      ...config.value.filters,
      [field]: { value, matchMode },
    };

    const event = createPrimevueEvent(filters);
    return onFilterChange(event, shouldReload);
  };

  const setEndpoint = (endpointXhr: string): void => {
    endpoint.value = endpointXhr;
  };

  type OrderDirection = 'asc' | 'desc';

  const setDefaultOrder = (field: string, direction: OrderDirection): void => {
    config.value.sort = { field, direction };
  };

  const reset = async (shouldReload: boolean = false): Promise<void> => {
    resetDataTable();
    if (shouldReload && endpoint.value) {
      data.value = await getData();
    }
  };

  const addData = <T>(item: T): void => {
    const { perPage } = config.value;

    if (!data.value) {
      data.value = {
        items: [ item ] as T[],
        meta: {
          from: 1,
          to: 1,
          total: 1,
          perPage,
          count: 1,
          lastPage: 1,
        },
      } as unknown as DataTableData<T>;
      return;
    }

    const items = data.value.items as T[];
    const prevTotal = data.value.meta?.total ?? items.length;
    const nextTotal = prevTotal + 1;

    items.unshift(item);

    if (items.length > perPage) {
      items.pop();
    }

    const count = items.length;
    const from = 1;
    const to = Math.min(from + count - 1, nextTotal);
    const lastPage = Math.max(1, Math.ceil(nextTotal / perPage));

    data.value.meta = {
      from,
      to,
      total: nextTotal,
      perPage,
      count,
      lastPage,
    };
  };

  const updateDataById = <T extends Record<string, unknown>>(
    id: string | number,
    next: T,
  ): void => {
    if (!data.value) {
      return;
    }

    const key = config.value.dataKey || 'id';
    const items = data.value.items as T[];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const index = items.findIndex((it) => (it as any)[key] === id);

    if (index >= 0) {
      items.splice(index, 1, next);
    }
  };

  return {
    endpoint,
    config,
    data,
    setData,
    getApiConfig,
    addFilter,
    createPrimevueEvent,
    getData,
    onPageChange,
    onSortChange,
    onFilterChange,
    setEndpoint,
    setDefaultOrder,
    reset,
    addData,
    updateDataById,
    setDataKey,
  };
});
